// © 2026 AttainIP Engineering
import { DetailGrid, type DetailRow } from './DetailGrid';

/**
 * Expanded detail for a Service Health row.
 *
 * Renders the RFC-0010 ``/admin/env`` payload — tier, raw SERVICE_ENV,
 * attain-* lib versions, build provenance — plus the legacy ``/admin/health``
 * fields (healthy flag, db schema, raw service name/version) that the
 * dashboard's main table only summarises.
 *
 * Pass ``detail`` (the per-service entry under ``/admin/v1/services``
 * ``services[name]``) — the env block is read off ``detail.env`` and
 * may be absent for services that haven't adopted RFC-0010 yet.
 */
export function ServiceDetail({ detail }: { detail: Record<string, any> | undefined }) {
  if (!detail) {
    return <p className="text-xs text-gray-500">No detail available — service did not respond.</p>;
  }
  const env = detail.env as
    | {
        service?: string;
        service_version?: string;
        tier?: string;
        service_env_raw?: string;
        libs?: Record<string, string>;
        build?: { git_sha?: string; built_at?: string };
      }
    | undefined;
  const libs = env?.libs ? Object.entries(env.libs) : [];
  const db = detail.db as { current_version?: string; version?: string; applied_at?: string } | undefined;

  const rows: DetailRow[] = [
    ['service', env?.service ?? detail.service],
    ['version', env?.service_version ?? detail.version],
    ['tier', env?.tier],
    ['service_env_raw', env?.service_env_raw],
    ['healthy', detail.healthy === true ? 'true' : detail.healthy === false ? 'false' : null],
    ['db.current_version', db?.current_version ?? db?.version],
    ['db.applied_at', db?.applied_at],
    ['build.git_sha', env?.build?.git_sha],
    ['build.built_at', env?.build?.built_at],
    [
      'libs',
      libs.length > 0 ? (
        <div className="space-y-0.5">
          {libs.map(([name, version]) => (
            <div key={name}>
              <span className="text-gray-500">{name}</span>
              <span className="text-gray-800">@{String(version)}</span>
            </div>
          ))}
        </div>
      ) : null,
    ],
    ['error', detail.error],
  ];
  return <DetailGrid rows={rows} />;
}
